import { storeWrapper } from '../storeWrapper'
import { debounce, throttle } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { toEndOfDayIsoString } from '@repo/utils/time'
import { ospreyFetchAllTimersSchema, ospreyFetchAllPostingsSchema } from '@repo/validation/schema_ospreyAPI'
import { useMattersStore } from '@repo/store/useMattersStore'

const initialState = {
  timeRecordingDrawerIsOpen: false,
  timeRecordingFormToDisplay: null,
  timeRecordingClientMatterNo: null,
  timeRecordingMatter: null,
  activeTimer: null,
  allTimers: [],
  allTimersRowCount: 0,
  allTimersApiParams: null,
  allPostings: [],
  allPostingsRowCount: 0,
  allPostingsApiParams: null,
  activities: [],
  jCodePhaseTaskCodes: [],
  jCodeActivityCodes: [],
  attendanceTextOptions: [],
  feeEarnerGradeCode: null,
  timeUnitType: null,
  fetchingActivities: false
}

export const useTimeRecordingStore = storeWrapper('useTimeRecordingStore', (set, get) => ({
  ...initialState,
  setTimeRecordingDrawerIsOpen: (timeRecordingDrawerIsOpen) => {
    set({ timeRecordingDrawerIsOpen })
  },
  setTimeRecordingFormToDisplay: async (timeRecordingFormToDisplay, clientMatterNo) => {
    if (timeRecordingFormToDisplay) {
      const { currentClientMatterNo, matters, fetchMatter } = useMattersStore.getState()
      const timeRecordingClientMatterNo = clientMatterNo || currentClientMatterNo || null
      let timeRecordingMatter = null

      // if we don't have the data for the matter, fetch it
      if (timeRecordingClientMatterNo && matters && !matters[timeRecordingClientMatterNo]) {
        await fetchMatter({ clientMatterNo: timeRecordingClientMatterNo })
      }

      // if we have the data, set it
      if (timeRecordingClientMatterNo && matters && matters[timeRecordingClientMatterNo]) {
        timeRecordingMatter = matters[timeRecordingClientMatterNo]
      }

      set({
        timeRecordingClientMatterNo,
        timeRecordingMatter
      })
    } else {
      set({
        timeRecordingClientMatterNo: null,
        timeRecordingMatter: null,
        timeRecordingFormToDisplay: null
      })
    }

    set({ timeRecordingFormToDisplay })
  },
  setAllTimersApiParams: ({ startAtIndex = 0, pageSize = 50, endDate = new Date(), clientNo, matterNo }) => {
    const allTimersApiParams = {
      startAtIndex,
      pageSize,
      endDate: toEndOfDayIsoString({ date: endDate })
    }

    if (clientNo) allTimersApiParams.clientNo = clientNo
    if (matterNo || matterNo === 0) allTimersApiParams.matterNo = matterNo

    set({ allTimersApiParams })
  },
  fetchAllTimers: async ({ append = false } = {}) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllTimers`,
        delay: 300,
        callback: async () => {
          if (!get().allTimersApiParams) get().setAllTimersApiParams({})

          const fetchedTimers = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'MatterTimer/GetAllExtendedByCurrentUser',
            data: get().allTimersApiParams,
            schema: ospreyFetchAllTimersSchema,
            method: 'POST'
          })

          const { rows, totalRowCount } = fetchedTimers
          let activeTimer = get().activeTimer || null
          const currentParams = get().allTimersApiParams
          const pageSize = currentParams.pageSize || 50

          let processedRows = rows || []
          let newStartAtIndex = 0

          if (rows && rows.length > 0) {
            const mappedRows = rows.map((timer) => {
              timer.isPlaying = timer.timerStarted && !timer.timerPaused
              if (timer.isPlaying) activeTimer = timer
              return timer
            })

            const firstTimer = mappedRows[0]
            const lastTimer = mappedRows[mappedRows.length - 1]
            const firstDate = firstTimer.timerPaused?.split('T')[0]
            const lastDate = lastTimer.timerPaused?.split('T')[0]

            const shouldSkipFiltering = firstDate === lastDate || rows.length < pageSize

            if (shouldSkipFiltering) {
              processedRows = mappedRows
              newStartAtIndex = mappedRows.length
            } else {
              processedRows = mappedRows.filter((row) => !row.timerPaused?.startsWith(lastDate))
              newStartAtIndex = processedRows.length
            }
          }

          const existingTimers = append ? get().allTimers : []
          const allTimers = [...existingTimers, ...processedRows]

          set({
            activeTimer,
            allTimers,
            allTimersRowCount: totalRowCount,
            allTimersApiParams: {
              ...currentParams,
              startAtIndex: (currentParams.startAtIndex || 0) + newStartAtIndex
            }
          })

          if (!totalRowCount) set({ activeTimer: null })

          const hasMore = rows?.length >= pageSize

          resolve({ hasMore })
        }
      })
    })
  },
  setAllPostingsApiParams: ({ startAtIndex = 0, pageSize = 50, postingDate = new Date(), clientNo, matterNo }) => {
    const allPostingsApiParams = {
      startAtIndex,
      pageSize,
      postingDate: toEndOfDayIsoString({ date: postingDate })
    }

    if (clientNo) allPostingsApiParams.clientNo = clientNo
    if (matterNo || matterNo === 0) allPostingsApiParams.matterNo = matterNo

    set({ allPostingsApiParams })
  },
  fetchAllPostings: async ({ append = false } = {}) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllPostings`,
        delay: 300,
        callback: async () => {
          if (!get().allPostingsApiParams) get().setAllPostingsApiParams({})

          const fetchedPostings = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeLedgerPosting/GetAllTimeLedgerPostingExtendedBySearchCriteria',
            data: get().allPostingsApiParams,
            schema: ospreyFetchAllPostingsSchema,
            method: 'POST'
          })

          const { rows, totalRowCount } = fetchedPostings
          const currentParams = get().allPostingsApiParams
          const pageSize = currentParams.pageSize || 50

          let processedRows = rows || []
          let newStartAtIndex = 0

          if (rows && rows.length > 0) {
            const firstPosting = rows[0]
            const lastPosting = rows[rows.length - 1]
            const firstDate = firstPosting.postingDate?.split('T')[0]
            const lastDate = lastPosting.postingDate?.split('T')[0]

            const shouldSkipFiltering = firstDate === lastDate || rows.length < pageSize

            if (shouldSkipFiltering) {
              processedRows = rows
              newStartAtIndex = rows.length
            } else {
              processedRows = rows.filter((row) => !row.postingDate?.startsWith(lastDate))
              newStartAtIndex = processedRows.length
            }
          }

          const existingPostings = append ? get().allPostings : []
          const allPostings = [...existingPostings, ...processedRows]

          set({
            allPostings,
            allPostingsRowCount: totalRowCount,
            allPostingsApiParams: {
              ...currentParams,
              startAtIndex: (currentParams.startAtIndex || 0) + newStartAtIndex
            }
          })

          const hasMore = rows?.length >= pageSize

          resolve({ hasMore })
        }
      })
    })
  },
  fetchActiveTimer: async () => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchActiveTimer`,
        callback: async () => {
          const previousActiveTimer = get().activeTimer

          try {
            const activeTimer = await ospreyAPI({
              service: 'timerecording',
              endpoint: 'MatterTimer/GetExtendedActiveByCurrentUser',
              suppressErrorToast: true
            })

            activeTimer.isPlaying = activeTimer.timerStarted && !activeTimer.timerPaused

            const allTimersClone = get().allTimers.map((timer) => {
              if (timer.matterTimerId === activeTimer.matterTimerId) {
                return activeTimer
              }

              timer.isPlaying = false
              return timer
            })

            set({ activeTimer, allTimers: allTimersClone })
          } catch (err) {
            if (get().allTimers?.length) set({ activeTimer: previousActiveTimer || null })
          }

          resolve()
        }
      })
    })
  },
  createPosting: async (data) => {
    return new Promise((resolve) => {
      throttle({
        id: `createPosting`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeLedgerPosting/CreateTimeLedgerPostings',
            data,
            method: 'POST'
          })

          resolve(res)
        }
      })
    })
  },
  createTimer: async ({
    clientNo,
    matterNo,
    userId,
    remunerationTypeId,
    timeRecordingActivityId,
    description,
    attendanceNote
  }) => {
    throttle({
      id: `createTimer`,
      callback: async () => {
        await ospreyAPI({
          service: 'timerecording',
          endpoint: 'MatterTimer/Create',
          data: {
            clientNo,
            matterNo: Number(matterNo),
            userId,
            remunerationTypeId,
            timeRecordingActivityId,
            description,
            attendanceNote
          },
          method: 'POST'
        })

        const currentTimersParams = get().allTimersApiParams
        const currentPostingsParams = get().allPostingsApiParams

        if (currentTimersParams) {
          set({
            allTimersApiParams: {
              ...currentTimersParams,
              startAtIndex: 0
            }
          })
        }

        if (currentPostingsParams) {
          set({
            allPostingsApiParams: {
              ...currentPostingsParams,
              startAtIndex: 0
            }
          })
        }

        const endDate = new Date()
        endDate.setUTCHours(23, 59, 59, 999)
        get().fetchAllTimers({ startAtIndex: 0, pageSize: 100, endDate })
      }
    })
  },
  postTimer: async (data) => {
    return new Promise((resolve) => {
      throttle({
        id: `postTimer`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'MatterTimer/Post',
            data,
            method: 'POST'
          })

          const endDate = new Date()
          endDate.setUTCHours(23, 59, 59, 999)
          get().fetchAllTimers({ startAtIndex: 0, pageSize: 25, endDate })
          resolve(res)
        }
      })
    })
  },
  resumeTimer: async ({ matterTimerId }) => {
    throttle({
      id: `resumeTimer`,
      callback: async () => {
        const previousTimer = get().activeTimer
        if (previousTimer && previousTimer.matterTimerId !== matterTimerId) {
          previousTimer.isPlaying = false
          set({ activeTimer: { ...previousTimer } })
        }

        await ospreyAPI({
          service: 'timerecording',
          endpoint: 'MatterTimer/Resume',
          data: { matterTimerId },
          method: 'PUT',
          sendAsUrlParams: true
        })

        const currentTimersParams = get().allTimersApiParams
        const currentPostingsParams = get().allPostingsApiParams

        if (currentTimersParams) {
          set({
            allTimersApiParams: {
              ...currentTimersParams,
              startAtIndex: 0
            }
          })
        }

        if (currentPostingsParams) {
          set({
            allPostingsApiParams: {
              ...currentPostingsParams,
              startAtIndex: 0
            }
          })
        }

        const endDate = new Date()
        endDate.setUTCHours(23, 59, 59, 999)
        get().fetchAllTimers({ startAtIndex: 0, pageSize: 100, endDate })
      }
    })
  },
  pauseTimer: async ({ matterTimerId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `pauseTimer`,
        callback: async () => {
          const pausedTimer = get().activeTimer
          if (pausedTimer) {
            pausedTimer.isPlaying = false
          }

          set({ activeTimer: { ...pausedTimer } })

          await ospreyAPI({
            service: 'timerecording',
            endpoint: 'MatterTimer/Pause',
            data: { matterTimerId },
            method: 'PUT',
            sendAsUrlParams: true
          })

          const allTimersClone = get().allTimers.map((timer) => {
            if (timer.matterTimerId === matterTimerId) {
              return { ...timer, isPlaying: false, timerPaused: new Date().toISOString() }
            }
            return timer
          })

          set({ allTimers: allTimersClone })
          resolve()
        }
      })
    })
  },
  deleteTimer: async ({ matterTimerId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `deleteTimer`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'MatterTimer/Delete',
            data: { matterTimerId },
            method: 'DELETE',
            sendAsUrlParams: true
          })

          const endDate = new Date()
          endDate.setUTCHours(23, 59, 59, 999)
          await get().fetchAllTimers({ startAtIndex: 0, pageSize: 25, endDate })
          resolve(res)
        }
      })
    })
  },
  renderTimerTime: ({ timer }) => {
    if (!timer) return '0m'

    const { secondsRun, runningTotalInSeconds } = timer

    let timeInSeconds = runningTotalInSeconds || secondsRun || 0
    return get().formatTime(timeInSeconds)
  },
  calculateSeconds: (timerStarted, secondsAlreadyTracked = 0) => {
    let timeInSeconds = secondsAlreadyTracked
    const utcDate = timerStarted.includes('Z') ? timerStarted : timerStarted + 'Z'
    const startTime = new Date(utcDate)
    const deltaMs = Date.now() - startTime
    timeInSeconds = Math.floor(timeInSeconds + deltaMs / 1000)
    return timeInSeconds
  },
  updateActiveTimerSeconds: () => {
    const timer = get().activeTimer
    if (!timer) return

    const { secondsRun, timerStarted, timerPaused } = timer

    let timeInSeconds = secondsRun || 0

    if (timerStarted && !timerPaused) {
      timeInSeconds = get().calculateSeconds(timerStarted, timeInSeconds)
    }

    set({ activeTimer: { ...timer, runningTotalInSeconds: timeInSeconds } })
  },
  renderPostingTime: ({ posting }) => {
    const { timeUnits, timeMinutes } = posting

    if (timeUnits) {
      return `${timeUnits || 0} unit${timeUnits === 1 ? '' : 's'}`
    }

    const timeInSeconds = timeMinutes * 60
    return get().formatTime(timeInSeconds)
  },

  formatTime: (timeInSeconds) => {
    if (!timeInSeconds) return '0m'

    const hours = Math.floor(timeInSeconds / 3600)
    const roundedHoursInMinutes = hours ? hours * 60 : 0
    const minutes = Math.floor(timeInSeconds / 60 - roundedHoursInMinutes)
    let seconds = Math.floor(timeInSeconds - (hours * 3600 + minutes * 60))

    if (hours) {
      return `${hours}h ${minutes}m`
    }
    if (minutes) {
      return `${minutes}m ${seconds}s`
    }

    return `${seconds}s`
  },
  fetchActivities: async ({ privateOrLegalAid, workflowTypeId, crmSchemeId, remunerationTypeId, branchId }) => {
    throttle({
      id: `fetchActivities-${privateOrLegalAid}-${workflowTypeId}-${crmSchemeId}-${remunerationTypeId}-${branchId}`,
      callback: async () => {
        set({ activities: [], fetchingActivities: true })

        let endpoint
        let payload

        if (privateOrLegalAid === 'P') {
          endpoint = 'WorkflowTypeTimeRecordingActivity/GetAllExtendedByWorkflowTypeId'
          payload = { workflowTypeId }
        } else if (privateOrLegalAid === 'L') {
          endpoint = 'RemunerationTypeTimeRecordingActivity/GetAllExtendedByRemunerationTypeIdAndBranchIdAndCrmSchemeId'
          payload = { crmSchemeId, remunerationTypeId, branchId }
        } else {
          throw 'Missing privateOrLegalAid'
        }

        const activities = await ospreyAPI({
          service: 'timerecording',
          endpoint,
          data: payload
        })

        set({ activities, fetchingActivities: false })
      }
    })
  },
  fetchFeeEarnerGradeCode: async ({ feeEarnerId }) => {
    throttle({
      id: `fetchFeeEarnerGradeCode`,
      callback: async () => {
        set({ feeEarnerGradeCode: null })

        try {
          const feeEarnerGradeCode = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'FeeEarnerGrade/GetCurrentCodeByFeeEarnerId',
            data: { feeEarnerId },
            suppressErrorToast: true // the endpoint can return a 404 if the fee earner has no grade code, which is fine and we don't want to show an error
          })

          set({ feeEarnerGradeCode })
        } catch (err) {
          set({ feeEarnerGradeCode: null })
        }
      }
    })
  },
  setFeeEarnerGradeCode: ({ feeEarnerGradeCode }) => {
    set({ feeEarnerGradeCode })
  },
  fetchJCodePhaseTaskCodes: async () => {
    throttle({
      id: `fetchJCodePhaseTaskCodes`,
      callback: async () => {
        set({ jCodePhaseTaskCodes: [] })

        const jCodePhaseTaskCodes = await ospreyAPI({
          service: 'timerecording',
          endpoint: 'JCodePhaseTask/GetAll'
        })

        set({ jCodePhaseTaskCodes })
      }
    })
  },
  fetchJCodeActivityCodes: async () => {
    throttle({
      id: `fetchJCodeActivityCodes`,
      callback: async () => {
        set({ jCodeActivityCodes: [] })

        const jCodeActivityCodes = await ospreyAPI({
          service: 'timerecording',
          endpoint: 'JCodeActivity/GetAll'
        })

        set({ jCodeActivityCodes })
      }
    })
  },
  fetchTimerJCodePhaseLimitWarning: (data) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchJCodePhaseLimitWarning`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'MatterTimer/GetJCodePhaseLimitWarning',
            method: 'POST',
            data
          })

          if (res?.status === 204) {
            resolve(null)
          } else {
            resolve(res)
          }
        }
      })
    })
  },
  fetchPostingJCodePhaseLimitWarning: (data) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchPostingJCodePhaseLimitWarning`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeLedgerPosting/GetJCodePhaseLimitWarning',
            method: 'POST',
            data
          })

          if (res?.status === 204) {
            resolve(null)
          } else {
            resolve(res)
          }
        }
      })
    })
  },
  fetchClientMatterCreditLimitWarnings: (data) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchClientMatterWipLimitWarning`,
        callback: async () => {
          const res = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeLedgerPosting/GetClientMatterCreditLimitWarnings',
            method: 'POST',
            data
          })

          if (res?.status === 204) {
            resolve(null)
          } else {
            resolve(res)
          }
        }
      })
    })
  },
  fetchAttendanceText: ({ workflowTypeId }) => {
    throttle({
      id: `fetchAttendanceText-${workflowTypeId}`,
      callback: async () => {
        set({ attendanceTextOptions: [] })

        const options = await ospreyAPI({
          service: 'timerecording',
          endpoint: 'AttendanceText/GetAllByWorkflowTypeId',
          data: { workflowTypeId }
        })

        const attendanceTextOptions = options?.filter((opt) => !!opt.attendanceNote)

        set({ attendanceTextOptions })
      }
    })
  },
  fetchTimeUnitType: () => {
    throttle({
      id: `fetchTimeUnitType`,
      callback: async () => {
        const timeUnitType = await ospreyAPI({
          service: 'lookups',
          endpoint: 'SystemConfiguration/GetTimeUnitType'
        })

        set({ timeUnitType })
      }
    })
  },
  fetchRate: ({ clientNo, matterNo, feeEarnerId, timeRecordingActivityId, remunerationTypeId, letterId = null }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchTimeUnitType-${timeRecordingActivityId}-${remunerationTypeId}-${feeEarnerId}-${clientNo}`,
        callback: async () => {
          const { rateType, rateAmount, timeAmount } = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeRecordingActivity/GetRate',
            data: { clientNo, matterNo, feeEarnerId, timeRecordingActivityId, remunerationTypeId, letterId }
          })

          resolve({ rateType, rateAmount, timeAmount })
        }
      })
    })
  },
  fetchRateCalculation: async ({ rateType, rateAmount, timeAmount, unitQuantity, timeQuantity }) => {
    // already debounced in the UI

    return new Promise((resolve) => {
      throttle({
        id: `fetchRateCalculation-${rateType}-${rateAmount}-${timeAmount}-${unitQuantity}-${timeQuantity}`,
        callback: async () => {
          const total = await ospreyAPI({
            service: 'timerecording',
            endpoint: 'TimeLedgerPosting/CalculateValue',
            data: { rateType, rateAmount, timeAmount, unitQuantity, timeQuantity }
          })

          resolve(total)
        }
      })
    })
  },
  reset: () => {
    set(initialState)
  }
}))
